import React, { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { DailyLog } from "../types";
import { DailyLogCard } from "./DailyLogCard";

interface DailyLogsListProps {
  logs: DailyLog[];
  hasMore: boolean;
  isLoading: boolean;
  loadMore: () => void;
  onOpen: (log: DailyLog) => void;
  projectIdToName?: Map<string, string>;
  selectedRows?: string[];
  isSelectionMode?: boolean;
  onSelectRow?: (logId: string, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
  onCancelSelection?: () => void;
}

export function DailyLogsList({ logs, hasMore, isLoading, loadMore, onOpen, projectIdToName, selectedRows = [], isSelectionMode = false, onSelectRow, onSelectAll, onCancelSelection }: DailyLogsListProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        loadMore();
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  return (
    <div className="space-y-4">
      {isSelectionMode && logs.length > 0 && onSelectAll && (
        <div className="mb-2 flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSelectAll(selectedRows.length !== logs.length)}
            className="h-8 px-3"
          >
            {selectedRows.length === logs.length && logs.length > 0 ? 'Deselect All' : 'Select All'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onCancelSelection}
            className="h-8 px-3"
          >
            Cancel
          </Button>
        </div>
      )}
      {logs.map((log) => (
        <DailyLogCard 
          key={log.id} 
          log={log} 
          onOpen={onOpen} 
          projectIdToName={projectIdToName}
          isSelected={selectedRows.includes(log.id)}
          showCheckbox={isSelectionMode}
          onSelect={(checked) => onSelectRow?.(log.id, checked)}
        />
      ))}
      <div ref={sentinelRef} />
      {isLoading && <div className="text-sm text-gray-500">Loading…</div>}
      {!hasMore && <div className="text-sm text-gray-400">No more logs</div>}
    </div>
  );
}


